/**
 * Currency code to symbol map for stores that pass currency as a string code.
 */
const CURRENCY_SYMBOLS = {
  EUR: "€",
  USD: "$",
  GBP: "£"
};

/**
 * Resolves store currency (string code or object with symbol) to display symbol.
 * @param {string|{ symbol?: string, code?: string }|null} currency - Store currency from API.
 * @returns {string} Currency symbol (e.g. "€", "$").
 */
export function getCurrencySymbolFromStore(currency) {
  if (!currency) return "$";
  if (typeof currency === "string")
    return CURRENCY_SYMBOLS[currency] || currency;
  if (currency.symbol) return currency.symbol;
  if (currency.code) return CURRENCY_SYMBOLS[currency.code] || currency.code;
  return "$";
}

/**
 * Whether the store uses Euro (for European number formatting: comma as decimal).
 * @param {string|{ code?: string }|null} currency - Store currency from API.
 * @returns {boolean}
 */
export function isEuroCurrency(currency) {
  if (!currency) return false;
  const code = typeof currency === "string" ? currency : currency?.code;
  return code === "EUR";
}

/**
 * Formats a numeric price for display (European format for EUR: comma decimal, symbol after).
 * @param {number} value - Numeric price.
 * @param {string} symbol - Currency symbol (e.g. "€", "$").
 * @param {boolean} isEur - Use European format (comma as decimal separator).
 * @returns {string} Formatted price string (e.g. "12,50 €" or "$12.50").
 */
export function formatPrice(value, symbol, isEur) {
  const num = Number(value);
  if (!Number.isFinite(num)) return isEur ? "0,00 " + symbol : symbol + "0.00";
  const fixed = num.toFixed(2);
  if (isEur) {
    const withComma = fixed.replace(".", ",");
    return withComma + " " + symbol;
  }
  return symbol + fixed;
}

/**
 * Formats a location object into a readable address string.
 * @param {Object} location - The location object with optional properties.
 * @returns {string} The formatted address.
 */
export function formatLocationAddress(location) {
  if (!location) return "";

  let address = location.address || "";
  if (location.city) {
    address = address ? `${address}, ${location.city}` : location.city;
  }
  if (location.state) {
    address = address ? `${address}, ${location.state}` : location.state;
  }
  if (location.zipcode) {
    address = address ? `${address} ${location.zipcode}` : location.zipcode;
  }
  return address || "";
}
